import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { DESTINATIONS } from "@/app/destinations";
import { DesktopOnly } from "@/mobile/screens/DesktopOnly";
import { PhoneAdd } from "@/mobile/screens/PhoneAdd";
import { PhoneAsk } from "@/mobile/screens/PhoneAsk";
import { PhoneCaptureToken } from "@/mobile/screens/PhoneCaptureToken";
import { PhoneNeedsYou } from "@/mobile/screens/PhoneNeedsYou";
import { PhonePeople } from "@/mobile/screens/PhonePeople";
import { PhoneSearch } from "@/mobile/screens/PhoneSearch";
import { PhoneSubject } from "@/mobile/screens/PhoneSubject";
import { PhoneWorldView } from "@/mobile/screens/PhoneWorldView";
import { PHONE_HOME } from "@/mobile/phoneSurface";
import { Surface } from "@/mobile/Surface";
import { SimpleAdd } from "@/simple/SimpleAdd";
import { SimpleNeedsYou } from "@/simple/SimpleNeedsYou";
import { SimpleSubject } from "@/simple/SimpleSubject";
import { SimpleWorldView } from "@/simple/SimpleWorldView";
import { AssistantPage } from "@/pages/Assistant";
import { ChatPage } from "@/pages/Chat";
import { AuthorizeAgentPage } from "@/pages/AuthorizeAgent";
import { CapturePage, DestinationPage, SettingsPage } from "@/pages/DestinationPage";
import { EntitiesPage } from "@/pages/Entities";
import { EntityDetailPage } from "@/pages/EntityDetail";
import { InitiativeDetailPage } from "@/pages/InitiativeDetail";
import { InitiativesPage } from "@/pages/Initiatives";
import { InsightsPage } from "@/pages/Insights";
import { PeoplePage } from "@/pages/People";
import { PersonDetailPage } from "@/pages/PersonDetail";
import { ProcessingPage } from "@/pages/Processing";
import { ProcessingBatchPage } from "@/pages/ProcessingBatch";
import { RecordingDetailPage } from "@/pages/RecordingDetail";
import { RecordingsPage } from "@/pages/Recordings";
import { ReviewQueuePage } from "@/pages/ReviewQueue";
import { SearchPage } from "@/pages/Search";
import { TranscriptReviewPage } from "@/pages/TranscriptReview";
import { SourceDetailPage } from "@/pages/SourceDetail";
import { WorldViewPage } from "@/pages/WorldView";
import { WorldViewSubjectPage } from "@/pages/WorldViewSubject";
import { LedgerProvider } from "@/lib/LedgerContext";
import { SynthesisBatchesProvider } from "@/lib/SynthesisBatchesContext";
import { homeFor } from "@/lib/uiMode";
import { useUiMode } from "@/lib/uiMode";
import { AppShell } from "@/shell/AppShell";

/**
 * The desktop page each destination renders, the phone screen beside it, and — where UI-16
 * gives one — its simple-mode rendering.
 *
 * Every surface is served by this one route table (M12, extended in M13): a destination
 * cannot exist in a rail and be missing from the routes, and it cannot have a phone or a
 * simple screen at an address the desktop does not also answer. Where the phone app has no
 * design for a destination, the entry says so out loud with `<DesktopOnly/>` rather than
 * leaving a gap for a fallback to cover — `phoneSurface.ts` holds the reason each one is
 * desk work.
 *
 * A destination with no `simple` entry falls through to its desktop screen, which is
 * correct in both directions: Search reads the same in either mode, and the destinations
 * simple mode does not want are absent from its *rail* rather than reduced — their
 * addresses still resolve, because a link must not break for the mode it is opened in.
 */
const SCREENS: Record<
  string,
  { desktop: React.ReactNode; phone: React.ReactNode; simple?: React.ReactNode }
> = {
  recordings: { desktop: <RecordingsPage />, phone: <DesktopOnly /> },
  entities: { desktop: <EntitiesPage />, phone: <DesktopOnly /> },
  initiatives: { desktop: <InitiativesPage />, phone: <DesktopOnly /> },
  review: { desktop: <ReviewQueuePage />, phone: <PhoneNeedsYou />, simple: <SimpleNeedsYou /> },
  search: { desktop: <SearchPage />, phone: <PhoneSearch /> },
  people: { desktop: <PeoplePage />, phone: <PhonePeople /> },
  "world-view": { desktop: <WorldViewPage />, phone: <PhoneWorldView />, simple: <SimpleWorldView /> },
  insights: { desktop: <InsightsPage />, phone: <DesktopOnly /> },
  assistant: { desktop: <AssistantPage />, phone: <DesktopOnly /> },
  chat: { desktop: <ChatPage />, phone: <PhoneAsk />, simple: <ChatPage simple /> },
};

/**
 * The home address of whichever surface is on screen.
 *
 * Three different answers, and the differences are the point. The phone opens on the world
 * view because that is what the phone app is for; full mode opens on Recordings, at the head
 * of the pipeline; simple mode opens on the world view too, because it is the only one of
 * the two that its reader was ever promised.
 */
function Home() {
  const mode = useUiMode();

  return (
    <Surface
      desktop={<Navigate to={homeFor(mode)} replace />}
      phone={<Navigate to={PHONE_HOME} replace />}
    />
  );
}

/**
 * CAP-5 — the one Settings section the phone app draws.
 *
 * Registering the PC that runs Factorium Recorder is a thing to *do* rather than
 * configuration set up once, and it is done standing at that PC with a phone in the other
 * hand. Everything else under Settings is desk work and says so.
 *
 * It reads the section from the route rather than claiming an address of its own: a static
 * `/settings/capture` route would out-rank `/settings/:section` on the **desktop** too, and
 * `SettingsPage` reads that param to know which section it is showing.
 */
function PhoneSettingsSection() {
  const { section } = useParams<{ section: string }>();

  return section === "capture" ? <PhoneCaptureToken /> : <DesktopOnly />;
}

/**
 * One route per destination, all of them under the shell layout.
 *
 * The route table is generated from the same descriptor list the rail renders, so a
 * destination cannot exist in one and be missing from the other — which is the usual
 * way a rail button starts leading nowhere.
 */
export function AppRoutes({ initials }: { initials: string }) {
  return (
    // UI-9's one read, mounted above the routes so the Insights page and the shell's navigator
    // panel share it. It has to be inside the router — the query comes from the URL — and
    // AppProvider is above BrowserRouter, which is why this is a second provider rather than
    // another field on the app context. It fetches nothing until /insights is open.
    <LedgerProvider>
    {/* UI-24 — the same shape, one destination over: fetches nothing until /processing is
        open and developer mode is on. */}
    <SynthesisBatchesProvider>
      <Routes>
      {/* UI-12, and the only route that is not under the shell.
          Transcript Review is a reading view, not a destination: it takes the whole
          window, and a rail down the side of it would offer to take you somewhere else
          in the middle of a sentence. Declared first so it wins over the shell's own
          /recordings/:id, which would otherwise match nothing here but reads clearly
          this way round.

          M12 leaves it desktop-only, deliberately: word-level playback, a scrubbable
          waveform and inline segment correction over an hour of speech is the least
          thumb-shaped screen in the app. On a phone the address still resolves — a link
          into it should not break — and answers with the standalone notice, which brings
          its own page because there is no shell around this route to put one in. */}
      <Route
        path="/recordings/:id/review"
        element={<Surface desktop={<TranscriptReviewPage />} phone={<DesktopOnly standalone />} />}
      />

      {/* SEC-5 (M9.1), and outside the shell for the same reason Transcript Review is:
          somebody arriving here came from another application to answer one question, and a
          rail offering to take them somewhere else mid-decision is the wrong thing to put on
          the screen. It is one question and two buttons, so it needs no phone variant — it
          already fits a phone. */}
      <Route path="/authorize-agent" element={<AuthorizeAgentPage />} />

      <Route element={<AppShell initials={initials} />}>
        {/* Where the app opens. The two shells start in different places on purpose: the
            desktop's home is Recordings, at the head of the pipeline, and the phone's is
            the world view, which is what the phone app is for — the handoff's first tab. */}
        <Route index element={<Home />} />

        {/* A destination whose room is furnished replaces its placeholder here. The
            list is still the one source of rail buttons and routes, so a destination
            cannot exist in one and be missing from the other. */}
        {DESTINATIONS.map((destination) => {
          const screens = SCREENS[destination.id];

          return (
            <Route
              key={destination.id}
              path={destination.path}
              element={
                screens ? (
                  <Surface desktop={screens.desktop} phone={screens.phone} simple={screens.simple} />
                ) : (
                  <Surface desktop={<DestinationPage id={destination.id} />} phone={<DesktopOnly />} />
                )
              }
            />
          );
        })}

        {/* UI-11 — one entity in full. Under the shell like the recordings detail, and
            matched after the /entities list above by being the more specific path. */}
        <Route
          path="/entities/:id"
          element={<Surface desktop={<EntityDetailPage />} phone={<DesktopOnly />} />}
        />

        {/* UI-8 — one person's dossier. Its own address rather than /entities/:id, because
            a person is the one entity kind with everything else in the system pointing at
            it: recordings, insights, relations and the "is me" link. */}
        <Route
          path="/people/:id"
          element={<Surface desktop={<PersonDetailPage />} phone={<DesktopOnly />} />}
        />

        {/* UI-19 — one initiative's dossier. Its own address rather than /entities/:id, for
            the same reason a person's dossier has one (UI-8): an initiative is where the
            user works day to day rather than a record occasionally corrected. */}
        <Route
          path="/initiatives/:id"
          element={<Surface desktop={<InitiativeDetailPage />} phone={<DesktopOnly />} />}
        />

        {/* UI-10 — one subject in full. Addressed by its permanent slug rather than by an
            id, because that slug is the same address the wiki file, the search index and
            (later) chat citations use, and having two names for one page is how one of them
            goes stale. */}
        <Route
          path="/world-view/:slug"
          element={
            <Surface desktop={<WorldViewSubjectPage />} phone={<PhoneSubject />} simple={<SimpleSubject />} />
          }
        />

        {/* UI-24 — "Insight processing", developer-only (ARCH-8's rule: the address
            resolves either way, and the page itself draws the notice when the flag is off).
            Not generated from `DESTINATIONS` — it lives in the separate
            `DEVELOPER_DESTINATIONS` list so the rail's cold-load and overflow tests keep
            describing the ordinary rail. Registered explicitly here instead. */}
        <Route path="/processing" element={<Surface desktop={<ProcessingPage />} phone={<DesktopOnly />} />} />
        <Route
          path="/processing/:id"
          element={<Surface desktop={<ProcessingBatchPage />} phone={<DesktopOnly />} />}
        />

        {/* UI-5. Its own address rather than /sources/:id, because a recording is not
            a document: this screen plays audio, shows who spoke and will hold insights
            and entities, and none of that is true of an uploaded contract. */}
        <Route
          path="/recordings/:id"
          element={<Surface desktop={<RecordingDetailPage />} phone={<DesktopOnly />} />}
        />

        {/* Full mode has a route per capture kind and the route tells the Server which kind
            it is. Both reduced surfaces have one page whose file picker decides instead —
            the phone's (PhoneAdd) and simple mode's (SimpleAdd), over one shared set of
            rules in `useAddSource`. Both addresses land on it, so the manifest's "Add a
            source" shortcut works everywhere without a second entry pointing elsewhere. */}
        <Route
          path="/add"
          element={
            <Surface
              desktop={<Navigate to="/add/audio" replace />}
              phone={<PhoneAdd />}
              simple={<SimpleAdd />}
            />
          }
        />
        <Route
          path="/add/:kind"
          element={<Surface desktop={<CapturePage />} phone={<PhoneAdd />} simple={<SimpleAdd />} />}
        />

        {/* Where a duplicate prompt's "Open the original" lands (CAP-3). */}
        <Route path="/sources/:id" element={<Surface desktop={<SourceDetailPage />} phone={<DesktopOnly />} />} />

        <Route path="/settings" element={<Surface desktop={<SettingsPage />} phone={<DesktopOnly />} />} />
        <Route
          path="/settings/:section"
          element={<Surface desktop={<SettingsPage />} phone={<PhoneSettingsSection />} />}
        />

        {/* An unknown address lands somewhere real rather than on a blank frame. */}
        <Route path="*" element={<Home />} />
      </Route>
      </Routes>
    </SynthesisBatchesProvider>
    </LedgerProvider>
  );
}
